// Blob-first upload (szzt-spec.md §7.1). Every resource blob is uploaded and its returned
// CID verified against the build's `raw` CID BEFORE the commit, so the manifest can safely
// reference them. Uploads run sequentially with exponential backoff to respect PDS upload rate
// limits; re-uploading an already-present blob is idempotent and dedups (§7.1), so a retried or
// resumed run is safe.
import type { BuildResult } from "@szzt/core";
import type { PdsClient } from "./types.ts";

/** Thrown when a PDS returns a blob CID that does not match the build's `raw` CID (§7.2). */
export class BlobCidMismatchError extends Error {
  constructor(
    readonly path: string,
    readonly expected: string,
    readonly actual: string,
  ) {
    super(
      `uploadBlob returned CID ${actual} for ${path}, expected ${expected} — refusing to reference a non-matching blob`,
    );
    this.name = "BlobCidMismatchError";
  }
}

export class BlobSizeMismatchError extends Error {
  constructor(readonly path: string, readonly expected: number, readonly actual: number) {
    super(`uploadBlob returned size ${actual} for ${path}, expected ${expected} — refusing an inconsistent receipt`);
    this.name = "BlobSizeMismatchError";
  }
}

export interface UploadBlobsOptions {
  /** Max attempts per blob before giving up; defaults to 4. */
  maxAttempts?: number;
  /** Base backoff in ms (doubled each retry); defaults to 250. Set 0 in tests. */
  baseDelayMs?: number;
  /** Sleep hook (injectable for tests); defaults to real `setTimeout`. */
  sleep?: (ms: number) => Promise<void>;
}

const realSleep = (ms: number): Promise<void> =>
  ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();

/**
 * Upload every resource blob and verify its returned CID equals the build's `raw` CID (§7.2).
 * Rejects with {@link BlobCidMismatchError} on any mismatch (fatal — do not commit a bad ref).
 * Transient upload failures are retried with backoff; a CID mismatch is not retried.
 */
export async function uploadBlobs(
  client: PdsClient,
  built: BuildResult,
  opts: UploadBlobsOptions = {},
): Promise<void> {
  const maxAttempts = opts.maxAttempts ?? 4;
  const baseDelayMs = opts.baseDelayMs ?? 250;
  const sleep = opts.sleep ?? realSleep;
  if (!Number.isInteger(maxAttempts) || maxAttempts < 1 || !Number.isFinite(baseDelayMs) || baseDelayMs < 0) {
    throw new RangeError("upload retries require maxAttempts >= 1 and a non-negative finite baseDelayMs");
  }

  for (const blob of built.blobs) {
    let lastErr: unknown;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) await sleep(baseDelayMs * 2 ** (attempt - 1));
      let result: { cid: string; size: number };
      try {
        // Upload with the blob's canonical mimeType (§7.2): a conforming PDS content-sniffs and
        // stores this, and the manifest's blob ref declares the same string, so `applyWrites` is
        // accepted. Uploading a generic type would make the PDS store the sniffed type instead and
        // reject the ref (`InvalidMimeType`).
        result = await client.uploadBlob(blob.bytes, blob.blobMimeType);
      } catch (err) {
        lastErr = err;
        continue;
      }
      if (result.cid !== blob.cid) {
        // Integrity failure, not transient: a conforming PDS cannot return a different CID for
        // the same bytes, so retrying cannot help. Fail loudly.
        throw new BlobCidMismatchError(blob.path, blob.cid, result.cid);
      }
      if (result.size !== blob.size) throw new BlobSizeMismatchError(blob.path, blob.size, result.size);
      lastErr = undefined;
      break;
    }
    if (lastErr !== undefined) throw lastErr;
  }
}
